import { ChatMessage } from '@prisma/client'
import { Flex, Tag, Typography } from 'antd'
import dayjs from 'dayjs'
import React, { CSSProperties } from 'react'
import { styled } from 'styled-components'
import { OrganizationMemberFromContext } from '~/core/context/organization/hooks/useOrganizationMembers'
import { OrganizationType } from '~/features/organization/common'
import { OrganizationMemberClientShared } from '~/features/organizationMember/clientShared'
import { DesignBox } from '~/plugins/designbox'
import { Toolbox } from '~/plugins/toolbox/shared'
import { LoaderDots } from '../Thread/components/LoaderDots'
import { BadgeStatus } from './components/BadgeStatus'

const Wrapper = styled.li<any>`
  list-style: none;
  margin: 0;
  padding: 0;
  cursor: pointer;
  border-radius: 5px;
  background: ${props =>
    props.$isActive ? `rgba(0,0,0,0.06)` : `transparent`};

  &:hover {
    background: rgba(0, 0, 0, 0.06);
  }

  ${props => {
    if (props.$isActive) {
      return `
        background: rgba(0,0,0,0.06);
      `
    } else {
      return `
      
      `
    }
  }}
`

const getDate = (message: ChatMessage) => {
  if (!message?.createdAt) {
    return ''
  }
  const now = dayjs()
  const isToday = now.isSame(dayjs(message.createdAt), 'day')

  if (isToday) {
    return Toolbox.Date.toSlackTimeStyle(message.createdAt)
  } else {
    return Toolbox.Date.toAsanaStyle(message.createdAt)
  }
}

const COMMON_TEXT_STYLE: CSSProperties = {
  lineHeight: '18px',
}

type Props = {
  organization: OrganizationType
  organizationMember: OrganizationMemberFromContext
  message: ChatMessage
  isTyping: boolean
  countNotifications: number
  isActive?: boolean
  onClick: () => void
}

export const ContactItem: React.FC<Props> = ({
  organization,
  organizationMember,
  isTyping,
  message,
  countNotifications,
  isActive = false,
  onClick,
}) => {
  const name = OrganizationMemberClientShared.Helper.getName(organizationMember)
  const role = OrganizationMemberClientShared.Helper.getRole(organizationMember)
  const pictureUrl =
    OrganizationMemberClientShared.Helper.getPictureUrl(organizationMember)

  const content = message?.content ?? 'Start conversation'
  const preview = content?.length > 100 ? content.slice(0, 97) + '...' : content

  const date = getDate(message)

  return (
    <Wrapper $isActive={false} onClick={onClick}>
      <Flex flex={1} gap={8} align="center" className="p-1">
        <BadgeStatus
          organizationMember={organizationMember}
          translateX={38}
          translateY={42}
        >
          <DesignBox.UserAvatar
            pictureUrl={pictureUrl}
            propsAvatar={{
              size: 52,
              shape: 'square',
            }}
            renderAvatar={
              isTyping
                ? () => (
                    <Flex
                      style={{ width: '52px' }}
                      align="center"
                      justify="center"
                    >
                      <LoaderDots />
                    </Flex>
                  )
                : undefined
            }
          />
        </BadgeStatus>
        <Flex vertical style={{ overflow: 'hidden' }} flex={1}>
          <Flex justify="space-between" align="center">
            <Typography.Title level={4} style={{ margin: 0 }}>
              {role}
            </Typography.Title>
            <Typography.Text type="secondary" style={{ ...COMMON_TEXT_STYLE }}>
              {date}
            </Typography.Text>
          </Flex>

          <Flex>
            <Flex vertical style={{ overflow: 'hidden' }} flex={1}>
              <Typography.Text
                ellipsis
                type="secondary"
                style={{ ...COMMON_TEXT_STYLE }}
              >
                {name}: {preview}
              </Typography.Text>
            </Flex>

            {countNotifications > 0 && (
              <Flex vertical style={{ minWidth: '25px' }}>
                <div>
                  <Tag
                    color="red-inverse"
                    style={{
                      margin: 0,
                      borderRadius: '10px',
                      fontSize: '11px',
                      lineHeight: '15px',
                    }}
                  >
                    {countNotifications}
                  </Tag>
                </div>
              </Flex>
            )}
          </Flex>
        </Flex>
      </Flex>
    </Wrapper>
  )
}
